import { type NuxtAxiosInstance } from "@nuxtjs/axios";

import { type BubblesResponse, type CategoriesApi } from "@/types/apis/categories";
import { useApi } from "@/utils/api";

/**
 ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
 * @function useCategoriesApi
 *
 * @desc        A composable that exposes functions used to interact with the
 *              `PresentationService API`. The functions are wrappers around
 *              the useApi composable's request function. The returned functions
 *              can be destructured without losing any functionality.
 *
 * @summary     A composable used to interact with the PresentationService API.
 *
 ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
 *
 * @returns {CategoriesApi} Object for interacting with `PresentationService API`; see {@link CategoriesApi}.
 *
 ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
 *
 * @example <caption>Exposed actions of `useCategoriesApi`</caption>
 * ```ts
 *   this.$api.post("us");
 * ```
 *
 ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
 */

export default function useCategoriesApi($axios: NuxtAxiosInstance): CategoriesApi {
  const _api = useApi({
    name: "presentationService",
    url: `/v1/presentation/modules/v1_bubbles`,
    $axios,
  });

  async function post(locale: string) {
    return await _api.request<BubblesResponse>({
      method: "post",
      headers: {
        "x-ltk-prestaged": false,
        "x-ltk-feature-flags": "",
      },
      data: {
        country_code: locale?.toLocaleLowerCase() ?? "us",
        date: new Date().toISOString(),
        version: "1.0",
      },
    });
  }

  return { post };
}
